import React from 'react';
import { Link, withRouter, matchPath } from 'react-router-dom';
import { isEmpty } from 'lodash';
import { useTheme } from '@material-ui/core/styles';

/**
 * Filter menus based on their `disabled` permission function.
 */
const filterByPermissions = (menus, isParentLevel = true) => {
  return (menus || []).filter((item) => {
    if (isParentLevel && !isEmpty(item?.subMenus)) {
      const visibleSubmenus = filterByPermissions(item.subMenus, false);
      return !isEmpty(visibleSubmenus);
    }
    if (typeof item?.disabled === 'function') {
      return !item.disabled();
    }
    return true;
  });
};

const isValid = (_link, _pathname) => {
  if (!_pathname || !_link) return false;
  if (_link.startsWith('http')) return false;

  return Boolean(matchPath(_pathname, { path: _link, exact: false, strict: false }));
};

/**
 * Check if a link (or array of links) matches the current pathname.
 */
export const isActive = (links, pathname) => {
  return Array.isArray(links)
    ? links.some((link) => isValid(link, pathname) && !link?.startsWith('http'))
    : isValid(links, pathname);
};

/**
 * Single menu item — renders as a <Link> for internal routes or <a> for external.
 */
function HeaderMenuItem({ config, isOpacity, activeBorderWidth, activeBorderColor, isParentMenu, handleMenu, style, location }) {
  const theme = useTheme();

  const filteredSubMenus = isParentMenu ? filterByPermissions(config?.subMenus || [], false) : [];
  const toLink = isParentMenu && !isEmpty(filteredSubMenus) ? filteredSubMenus[0]?.link : config.link;
  const shouldHide = !isEmpty(config?.subMenus) && isEmpty(filteredSubMenus);
  const isExternalLink = config.link?.startsWith('http');
  const _isActive = !isExternalLink && isActive(
    config.subMenus ? config.subMenus.map((sub) => sub.link) : config.link,
    location.pathname,
  );

  const borderWidth = activeBorderWidth || 3;
  const borderColor = activeBorderColor || theme.palette.primary.main;

  const menuStyle = {
    fontFamily: theme.typography.fontFamily,
    margin: '0 20px',
    textDecoration: 'none',
    fontSize: '14px',
    whiteSpace: 'nowrap',
    fontWeight: 500,
    cursor: 'pointer',
    padding: '12px 8px',
    opacity: isOpacity ? 0.7 : 1,
    borderBottomStyle: 'solid',
    borderBottomWidth: borderWidth,
    borderBottomColor: _isActive ? borderColor : 'transparent',
    ...(_isActive && { opacity: 1 }),
    ...(style || {}),
  };

  if (shouldHide) return null;

  const testId = `header-menu-item-${config.label.toLowerCase().replace(/\s+/g, '-')}`;
  const onClick = () => (typeof handleMenu === 'function' ? handleMenu(isParentMenu) : null);

  // External links use <a>, internal links use <Link>
  if (isExternalLink) {
    const target = config.target && config.target !== '_self' ? config.target : undefined;
    return (
      <a
        href={toLink}
        style={menuStyle}
        onClick={onClick}
        target={target}
        rel={target ? 'noopener noreferrer' : undefined}
        data-testid={testId}
      >
        {config.label}
      </a>
    );
  }

  return (
    <Link
      to={toLink}
      style={menuStyle}
      onClick={onClick}
      data-testid={testId}
    >
      {config.label}
    </Link>
  );
}

/**
 * HeaderMenu — renders a list of menu items.
 * Must be wrapped with `withRouter` to get `location` prop (react-router v4).
 */
function HeaderMenu({ menus, isOpacity, activeBorderWidth, activeBorderColor, isParentMenu, handleMenu, style, location }) {
  const menuToRender = filterByPermissions(menus);

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {menuToRender.map((config) => (
        <HeaderMenuItem
          key={config.label}
          config={config}
          isOpacity={isOpacity}
          activeBorderWidth={activeBorderWidth}
          activeBorderColor={activeBorderColor}
          isParentMenu={isParentMenu || false}
          handleMenu={handleMenu}
          style={style}
          location={location}
        />
      ))}
    </div>
  );
}

export default withRouter(HeaderMenu);
